import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Upload, Plus, X, Trophy, TriangleAlert, Check, ArrowLeft, FileText } from "lucide-react";

const inr = (v) => v == null ? "—" : `₹ ${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function ProjectDetail() {
    const { id } = useParams();
    const { user, isAdmin, isApprover, hasPerm } = useAuth();
    const [project, setProject] = useState(null);
    const [quotations, setQuotations] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [items, setItems] = useState([]);
    const [comparison, setComparison] = useState(null);
    const [tab, setTab] = useState("overview");
    const [showQuotation, setShowQuotation] = useState(false);
    const [err, setErr] = useState("");
    const [bomBusy, setBomBusy] = useState(false);

    const canCompare = isAdmin() || isApprover();
    const canBom = hasPerm("bom.upload");

    const load = useCallback(async () => {
        try {
            const [p, q, v, i] = await Promise.all([
                api.get(`/projects/${id}`),
                api.get(`/projects/${id}/quotations`),
                api.get(`/vendors`),
                api.get(`/items`),
            ]);
            setProject(p.data);
            setQuotations(q.data);
            setVendors(v.data);
            setItems(i.data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    }, [id]);

    const loadComparison = useCallback(async () => {
        if (!canCompare) return;
        try {
            const { data } = await api.get(`/projects/${id}/comparison`);
            setComparison(data);
        } catch { /* */ }
    }, [id, canCompare]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (tab === "comparison") loadComparison(); }, [tab, loadComparison]);

    const onBomUpload = async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        setBomBusy(true);
        const fd = new FormData(); fd.append("file", f);
        try {
            await api.post(`/projects/${id}/bom-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            await load();
        } catch (err) {
            setErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally { setBomBusy(false); e.target.value = ""; }
    };

    if (!project) return <div className="text-stone-500 text-sm">Loading…</div>;

    // Determine which vendors this user can quote for
    const myAssignments = (project.vendor_assignments || []).filter(va => va.user_id === user?.id);
    const assignableVendors = (isAdmin() || isApprover()) ? vendors.filter(v => v.status !== "blacklisted")
        : vendors.filter(v => v.status !== "blacklisted" && myAssignments.some(a => a.vendor_id === v.id));

    return (
        <div data-testid="project-detail" className="space-y-6">
            <div>
                <Link to="/projects" className="text-sm text-stone-500 hover:text-stone-900 inline-flex items-center gap-1"><ArrowLeft size={14} /> Back to projects</Link>
                <div className="flex items-start justify-between mt-2">
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-stone-500">Project</div>
                        <div className="flex items-center gap-3 mt-1">
                            <h1 className="text-3xl font-heading font-semibold text-stone-900 tracking-tight">{project.name}</h1>
                            {project.status && (
                                <span className={"text-xs px-2 py-0.5 rounded-md border capitalize " + (
                                    project.status === "open" ? "bg-green-50 border-green-200 text-green-700" :
                                    project.status === "in_comparison" ? "bg-amber-50 border-amber-200 text-amber-800" :
                                    project.status === "awarded" ? "bg-blue-50 border-blue-200 text-blue-700" :
                                    "bg-stone-100 border-stone-200 text-stone-500")}>
                                    {project.status.replace("_", " ")}
                                </span>
                            )}
                        </div>
                        <div className="text-sm text-stone-500 mt-1 flex items-center gap-4">
                            {project.code && <span>Code: <span className="font-mono text-stone-700">{project.code}</span></span>}
                            {project.client_name && <span>Client: <span className="text-stone-800">{project.client_name}</span></span>}
                            {project.budget ? <span>Budget: <span className="text-stone-800">{inr(project.budget)}</span></span> : null}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        {canBom && (
                            <label className="cursor-pointer text-sm border border-stone-300 hover:bg-stone-50 rounded-md px-3 py-2 flex items-center gap-2">
                                <Upload size={14} /> {bomBusy ? "Uploading…" : ((project.bom || []).length ? "Replace BOM" : "Upload BOM")}
                                <input data-testid="bom-upload" type="file" accept=".xlsx,.csv" onChange={onBomUpload} className="hidden" disabled={bomBusy} />
                            </label>
                        )}
                        {hasPerm("quotation.create") && assignableVendors.length > 0 && (
                            <button data-testid="add-quotation-btn" onClick={() => setShowQuotation(true)} className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2">
                                <Plus size={14} /> Add quotation
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}

            {/* Empty-state banner for purchase users with no vendor assigned on this project. */}
            {!isAdmin() && !isApprover() && hasPerm("quotation.create") && assignableVendors.length === 0 && (
                <div data-testid="no-vendor-banner" className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                    <TriangleAlert size={18} className="text-amber-700 mt-0.5 shrink-0" />
                    <div className="text-sm">
                        <div className="font-medium text-amber-900">No vendor has been assigned to you on this project yet.</div>
                        <div className="text-amber-800 mt-1">Please contact your administrator to assign a vendor. Once assigned, you will be able to enter or upload your quotation from the top-right button.</div>
                    </div>
                </div>
            )}

            <div className="border-b border-stone-200 flex gap-6 text-sm">
                <button data-testid="tab-overview" onClick={() => setTab("overview")} className={"py-2 border-b-2 " + (tab === "overview" ? "border-amber-500 text-stone-900 font-medium" : "border-transparent text-stone-500")}>Overview & BOM</button>
                <button data-testid="tab-quotations" onClick={() => setTab("quotations")} className={"py-2 border-b-2 " + (tab === "quotations" ? "border-amber-500 text-stone-900 font-medium" : "border-transparent text-stone-500")}>Quotations ({quotations.length})</button>
                {canCompare && <button data-testid="tab-comparison" onClick={() => setTab("comparison")} className={"py-2 border-b-2 " + (tab === "comparison" ? "border-amber-500 text-stone-900 font-medium" : "border-transparent text-stone-500")}>Full comparison</button>}
                {(isAdmin() || user?.role === "full_access") && <button data-testid="tab-versions" onClick={() => setTab("versions")} className={"py-2 border-b-2 " + (tab === "versions" ? "border-amber-500 text-stone-900 font-medium" : "border-transparent text-stone-500")}>Version history</button>}
            </div>

            {tab === "overview" && (
                <div className="grid md:grid-cols-3 gap-4">
                    <div className="md:col-span-2 bg-white border border-stone-200 rounded-xl overflow-hidden">
                        <div className="px-5 py-4 border-b border-stone-200 font-heading font-semibold text-stone-900">BOM ({(project.bom || []).length} items)</div>
                        <div className="overflow-x-auto max-h-[500px]">
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                                    <tr>
                                        <th className="text-left py-2.5 px-4">Part code</th>
                                        <th className="text-left py-2.5 px-4">Description</th>
                                        <th className="text-left py-2.5 px-4">HSN</th>
                                        <th className="text-right py-2.5 px-4">Qty</th>
                                        <th className="text-left py-2.5 px-4">In catalog</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(project.bom || []).map((b, i) => (
                                        <tr key={i} className="border-t border-stone-100">
                                            <td className="py-2 px-4 font-mono text-xs text-stone-700">{b.part_code || "—"}</td>
                                            <td className="py-2 px-4 text-stone-700">{b.description}</td>
                                            <td className="py-2 px-4 text-stone-600 text-xs">{b.hsn_code || "—"}</td>
                                            <td className="py-2 px-4 text-right">{b.qty}</td>
                                            <td className="py-2 px-4">{b.in_catalog ? <span className="text-green-700 inline-flex items-center gap-1 text-xs"><Check size={12} /> Yes</span> : <span className="text-red-700 inline-flex items-center gap-1 text-xs"><TriangleAlert size={12} /> No</span>}</td>
                                        </tr>
                                    ))}
                                    {(project.bom || []).length === 0 && <tr><td colSpan={5} className="py-8 text-center text-stone-500 text-sm">No BOM uploaded yet.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <div className="bg-white border border-stone-200 rounded-xl p-5">
                            <div className="overline mb-2">Vendor assignments</div>
                            {(project.vendor_assignments || []).length === 0 ? <div className="text-sm text-stone-500">None assigned.</div> :
                                (project.vendor_assignments || []).map((va, i) => {
                                    const v = vendors.find(x => x.id === va.vendor_id);
                                    return <div key={i} className="text-sm text-stone-700 py-1"><span className="text-stone-500">User:</span> {va.user_id.slice(0, 8)}… → <span className="font-medium">{v?.name || va.vendor_id}</span></div>;
                                })
                            }
                        </div>
                        <div className="bg-white border border-stone-200 rounded-xl p-5">
                            <div className="overline mb-2">Notes</div>
                            <div className="text-sm text-stone-700 whitespace-pre-wrap">{project.notes || "—"}</div>
                        </div>
                    </div>
                </div>
            )}

            {tab === "quotations" && (
                <div className="space-y-4">
                    {!canCompare && (
                        <div className="text-xs text-stone-500 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                            Data isolation: you see only your own vendor&apos;s quotation. Administrator and approvers see all.
                        </div>
                    )}
                    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                                        <th className="text-left py-3 px-4">Vendor</th>
                                        <th className="text-left py-3 px-4">Version</th>
                                        <th className="text-right py-3 px-4">Lines</th>
                                        <th className="text-right py-3 px-4">Subtotal</th>
                                        <th className="text-right py-3 px-4">GST</th>
                                        <th className="text-right py-3 px-4">Total</th>
                                        <th className="text-right py-3 px-4">Mismatches</th>
                                        <th className="text-left py-3 px-4">Submitted</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {quotations.map((q) => (
                                        <tr key={q.id} className={"border-t border-stone-100 hover:bg-stone-50 " + (q.is_latest ? "" : "opacity-60")}>
                                            <td className="py-3 px-4 font-medium text-stone-900">{q.vendor_name}</td>
                                            <td className="py-3 px-4">
                                                <span className={"text-xs font-mono px-2 py-0.5 rounded-md border " + (q.is_latest ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-stone-100 border-stone-200 text-stone-500")}>
                                                    v{q.version || 1}{q.is_latest ? " · latest" : ""}
                                                </span>
                                                {q.source === "upload" && <span className="ml-2 text-[10px] uppercase tracking-wider text-stone-500">Uploaded</span>}
                                            </td>
                                            <td className="py-3 px-4 text-right">{q.lines?.length || 0}</td>
                                            <td className="py-3 px-4 text-right">{inr(q.subtotal)}</td>
                                            <td className="py-3 px-4 text-right">{inr(q.tax)}</td>
                                            <td className="py-3 px-4 text-right font-medium">{inr(q.total)}</td>
                                            <td className="py-3 px-4 text-right">
                                                {q.mismatch_count > 0 ? <span className="inline-flex items-center gap-1 text-red-700 text-xs"><TriangleAlert size={12} /> {q.mismatch_count}</span> : <span className="text-green-700 text-xs inline-flex items-center gap-1"><Check size={12} /> All match</span>}
                                            </td>
                                            <td className="py-3 px-4 text-xs text-stone-500">{q.created_at?.slice(0, 19).replace("T", " ")}</td>
                                        </tr>
                                    ))}
                                    {quotations.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-stone-500 text-sm">No quotations yet.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {tab === "comparison" && canCompare && (
                <ComparisonView comparison={comparison} projectId={id} onReload={loadComparison} />
            )}

            {tab === "versions" && (isAdmin() || user?.role === "full_access") && (
                <VersionHistoryView projectId={id} />
            )}

            {showQuotation && (
                <QuotationModal
                    project={project}
                    vendors={assignableVendors}
                    items={items}
                    onClose={() => setShowQuotation(false)}
                    onSaved={() => { setShowQuotation(false); load(); }}
                />
            )}
        </div>
    );
}

function VersionHistoryView({ projectId }) {
    const [data, setData] = React.useState(null);
    const [err, setErr] = React.useState("");
    React.useEffect(() => {
        api.get(`/projects/${projectId}/version-history`).then(r => setData(r.data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    }, [projectId]);
    if (err) return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>;
    if (!data) return <div className="text-stone-500 text-sm">Loading version history…</div>;
    if ((data.vendors || []).length === 0) return <div className="text-stone-500 text-sm bg-white border border-stone-200 rounded-xl p-8 text-center">No quotations yet — nothing to version.</div>;

    // Max number of versions across vendors, to size the column layout
    const maxVersions = Math.max(...data.vendors.map(v => v.versions.length));

    return (
        <div className="space-y-4">
            <div className="bg-white border border-stone-200 rounded-xl p-5">
                <div className="font-heading font-semibold text-stone-900 mb-1">Price movement per vendor</div>
                <div className="text-xs text-stone-500">Each column is one submitted version. Change is vs the previous version of the same vendor.</div>
            </div>
            <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                                <th className="text-left py-3 px-4 sticky left-0 bg-stone-50">Vendor</th>
                                {Array.from({ length: maxVersions }, (_, i) => (
                                    <th key={i} className="text-right py-3 px-4 whitespace-nowrap">v{i + 1}</th>
                                ))}
                                <th className="text-right py-3 px-4">Net change</th>
                                <th className="text-right py-3 px-4">Net %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.vendors.map(v => (
                                <tr key={v.vendor_id} className="border-t border-stone-100 hover:bg-stone-50">
                                    <td className="py-3 px-4 font-medium sticky left-0 bg-white">{v.vendor_name}</td>
                                    {Array.from({ length: maxVersions }, (_, i) => {
                                        const ver = v.versions[i];
                                        if (!ver) return <td key={i} className="py-3 px-4 text-right text-stone-300">—</td>;
                                        const change = ver.change_vs_prev;
                                        const isLatest = i === v.versions.length - 1;
                                        return (
                                            <td key={i} className={"py-3 px-4 text-right " + (isLatest ? "bg-amber-50 font-semibold" : "")}>
                                                <div>{inr(ver.total)}</div>
                                                {change != null && (
                                                    <div className={"text-xs " + (change < 0 ? "text-green-700" : change > 0 ? "text-red-700" : "text-stone-500")}>
                                                        {change < 0 ? "▼" : change > 0 ? "▲" : "•"} {inr(Math.abs(change))}
                                                    </div>
                                                )}
                                                <div className="text-[10px] text-stone-400 uppercase tracking-wider mt-1">{ver.created_at?.slice(0, 10)}{ver.source === "upload" ? " · upload" : ""}</div>
                                            </td>
                                        );
                                    })}
                                    <td className={"py-3 px-4 text-right font-semibold " + (v.net_change < 0 ? "text-green-700" : v.net_change > 0 ? "text-red-700" : "text-stone-500")}>
                                        {v.net_change < 0 ? "▼" : v.net_change > 0 ? "▲" : "•"} {inr(Math.abs(v.net_change || 0))}
                                    </td>
                                    <td className="py-3 px-4 text-right text-xs text-stone-600">{(v.net_change_pct || 0).toFixed(1)}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function ComparisonView({ comparison, projectId, onReload }) {
    if (!comparison) return <div className="text-stone-500 text-sm">Loading comparison…</div>;
    const { rows, vendor_summary, overall_winner_id, potential_saving, budget, budget_exceeded } = comparison;

    return (
        <div className="space-y-4">
            <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-white border border-stone-200 rounded-xl p-4">
                    <div className="overline">Vendors quoted</div>
                    <div className="text-2xl font-heading font-semibold text-stone-900 mt-1">{vendor_summary.length}</div>
                </div>
                <div className="bg-white border border-stone-200 rounded-xl p-4">
                    <div className="overline">Overall winner</div>
                    <div className="text-2xl font-heading font-semibold text-amber-700 mt-1 flex items-center gap-1"><Trophy size={20} />{vendor_summary.find(v => v.vendor_id === overall_winner_id)?.vendor_name || "—"}</div>
                </div>
                <div className="bg-white border border-stone-200 rounded-xl p-4">
                    <div className="overline">Potential saving</div>
                    <div className="text-2xl font-heading font-semibold text-green-700 mt-1">{inr(potential_saving)}</div>
                </div>
                <div className={"rounded-xl p-4 border " + (budget_exceeded ? "bg-red-50 border-red-200" : "bg-white border-stone-200")}>
                    <div className="overline">Budget check</div>
                    {budget ? (
                        <div className={"text-lg font-heading font-semibold mt-1 " + (budget_exceeded ? "text-red-700" : "text-green-700")}>
                            {budget_exceeded ? "Exceeded" : "Within"} · {inr(budget)}
                        </div>
                    ) : <div className="text-sm text-stone-500 mt-2">Not set</div>}
                </div>
            </div>

            <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
                    <div className="font-heading font-semibold text-stone-900">Line-by-line comparison</div>
                    <div className="text-xs text-stone-500">Scroll horizontally →</div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500 sticky top-0">
                                <th className="text-left py-3 px-4 border-r border-stone-200 sticky left-0 bg-stone-50">Part</th>
                                <th className="text-left py-3 px-4 border-r border-stone-200">HSN</th>
                                <th className="text-right py-3 px-4 border-r border-stone-200">Qty</th>
                                {vendor_summary.map(v => <th key={v.vendor_id} className="text-right py-3 px-4 border-r border-stone-200 whitespace-nowrap">{v.vendor_name}</th>)}
                                <th className="text-right py-3 px-4">Save vs next</th>
                                <th className="text-right py-3 px-4">Save vs avg</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={i} className="border-t border-stone-100">
                                    <td className="py-2.5 px-4 border-r border-stone-100 sticky left-0 bg-white">
                                        <div className="font-mono text-xs text-stone-800">{r.part_code}</div>
                                        <div className="text-xs text-stone-500">{r.description}</div>
                                    </td>
                                    <td className="py-2.5 px-4 border-r border-stone-100 text-xs text-stone-600">{r.hsn_code || "—"}</td>
                                    <td className="py-2.5 px-4 text-right border-r border-stone-100">{r.qty}</td>
                                    {vendor_summary.map(v => {
                                        const cell = r.vendors.find(x => x.vendor_id === v.vendor_id);
                                        const isCheapest = cell?.rate != null && r.cheapest_vendor_id === v.vendor_id;
                                        const mismatch = cell?.mismatch;
                                        return (
                                            <td key={v.vendor_id} className={"py-2.5 px-4 text-right border-r border-stone-100 " +
                                                (isCheapest ? "bg-green-50 text-green-800 font-semibold " : "") +
                                                (mismatch ? "bg-red-50 text-red-700" : "")}>
                                                {cell?.rate != null ? (
                                                    <div className="flex items-center justify-end gap-1">
                                                        {isCheapest && <Trophy size={12} className="text-amber-600" />}
                                                        {mismatch && <TriangleAlert size={12} className="text-red-600" />}
                                                        <span>{inr(cell.rate)}</span>
                                                    </div>
                                                ) : <span className="text-stone-300">—</span>}
                                            </td>
                                        );
                                    })}
                                    <td className="py-2.5 px-4 text-right text-green-700 text-xs">{r.saving_pct_next ? r.saving_pct_next.toFixed(1) + "%" : "—"}</td>
                                    <td className="py-2.5 px-4 text-right text-green-700 text-xs">{r.saving_pct_avg ? r.saving_pct_avg.toFixed(1) + "%" : "—"}</td>
                                </tr>
                            ))}
                            {/* Totals row */}
                            <tr className="bg-amber-50 border-t-2 border-amber-300 font-medium">
                                <td className="py-3 px-4 sticky left-0 bg-amber-50" colSpan={3}>Vendor totals</td>
                                {vendor_summary.map(v => (
                                    <td key={v.vendor_id} className={"py-3 px-4 text-right border-r border-amber-200 " + (v.vendor_id === overall_winner_id ? "text-amber-800 font-semibold" : "")}>
                                        {v.vendor_id === overall_winner_id && <Trophy size={12} className="inline mr-1 text-amber-600" />}
                                        {inr(v.total)}
                                    </td>
                                ))}
                                <td colSpan={2} className="py-3 px-4 text-right text-green-700">Saving: {inr(potential_saving)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function QuotationModal({ project, vendors, items, onClose, onSaved }) {
    const [vendorId, setVendorId] = useState("");
    const [mode, setMode] = useState("manual"); // manual | upload
    const [uploadFile, setUploadFile] = useState(null);
    const [lines, setLines] = useState(() => {
        // Prefill from BOM
        return (project.bom || []).map(b => ({
            part_code: b.part_code || "", description: b.description || "", hsn_code: b.hsn_code || "",
            qty: b.qty || 0, rate: 0, gst_rate: 18, uom: "Nos", notes: "",
        }));
    });
    const [notes, setNotes] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const addLine = () => setLines([...lines, { part_code: "", description: "", hsn_code: "", qty: 0, rate: 0, gst_rate: 18, uom: "Nos", notes: "" }]);
    const setLine = (i, patch) => { const arr = [...lines]; arr[i] = { ...arr[i], ...patch }; setLines(arr); };
    const removeLine = (i) => setLines(lines.filter((_, j) => j !== i));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            if (mode === "upload") {
                if (!uploadFile) throw new Error("Please choose a file");
                const fd = new FormData();
                fd.append("file", uploadFile);
                await api.post(`/quotations/upload?project_id=${encodeURIComponent(project.id)}&vendor_id=${encodeURIComponent(vendorId)}&notes=${encodeURIComponent(notes)}`,
                    fd, { headers: { "Content-Type": "multipart/form-data" } });
            } else {
                await api.post("/quotations", { project_id: project.id, vendor_id: vendorId, lines, notes });
            }
            onSaved();
        } catch (err) {
            setErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally { setBusy(false); }
    };

    const subtotal = lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.rate) || 0), 0);
    const tax = lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.rate) || 0) * (Number(l.gst_rate) || 0) / 100, 0);

    return (
        <div className="fixed inset-0 z-40 bg-stone-900/40 flex items-center justify-center p-4">
            <div className="bg-white border border-stone-200 rounded-xl max-w-5xl w-full max-h-[92vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div className="font-heading font-semibold text-stone-900">Add vendor quotation</div>
                    <button onClick={onClose} className="text-stone-500 hover:text-stone-900"><X size={18} /></button>
                </div>
                <form onSubmit={submit} className="p-6 space-y-4">
                    {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="overline block mb-1">Vendor</label>
                            <select data-testid="q-vendor" required value={vendorId} onChange={(e) => setVendorId(e.target.value)} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3">
                                <option value="">Select vendor</option>
                                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                            </select>
                            {vendors.length === 0 && <div className="text-xs text-red-700 mt-1">You have no assigned vendors for this project. Ask an administrator.</div>}
                        </div>
                        <div>
                            <label className="overline block mb-1">Entry method</label>
                            <div className="flex gap-2">
                                <button type="button" data-testid="mode-manual" onClick={() => setMode("manual")}
                                        className={"flex-1 border rounded-md text-sm py-2 px-3 " + (mode === "manual" ? "border-amber-500 bg-amber-50 text-amber-800 font-medium" : "border-stone-300 text-stone-600")}>
                                    Enter line items
                                </button>
                                <button type="button" data-testid="mode-upload" onClick={() => setMode("upload")}
                                        className={"flex-1 border rounded-md text-sm py-2 px-3 " + (mode === "upload" ? "border-amber-500 bg-amber-50 text-amber-800 font-medium" : "border-stone-300 text-stone-600")}>
                                    Upload xlsx/csv
                                </button>
                            </div>
                        </div>
                    </div>

                    {mode === "upload" ? (
                        <div className="border border-dashed border-stone-300 rounded-xl p-8 text-center bg-stone-50">
                            <input data-testid="q-upload-file" type="file" accept=".xlsx,.csv" onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                   className="block mx-auto text-sm" />
                            {uploadFile && <div className="mt-3 text-sm text-stone-700">Selected: <b>{uploadFile.name}</b></div>}
                            <div className="text-xs text-stone-500 mt-2">Columns can be named freely — the parser handles common variants (Part Code, Description, HSN, Qty, Rate, GST%). No row limit.</div>
                        </div>
                    ) : (
                        <>
                    <div className="border border-stone-200 rounded-xl overflow-x-auto">
                        <table className="w-full text-sm min-w-[900px]">
                            <thead className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                                <tr>
                                    <th className="text-left py-2.5 px-3">Part code</th>
                                    <th className="text-left py-2.5 px-3">Description</th>
                                    <th className="text-left py-2.5 px-3">HSN</th>
                                    <th className="text-right py-2.5 px-3">Qty</th>
                                    <th className="text-right py-2.5 px-3">Rate ₹</th>
                                    <th className="text-right py-2.5 px-3">GST %</th>
                                    <th className="text-right py-2.5 px-3">Amount</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((l, i) => (
                                    <tr key={i} className="border-t border-stone-100">
                                        <td className="p-1"><input list="part-codes" value={l.part_code} onChange={(e) => setLine(i, { part_code: e.target.value })} className="w-32 border border-stone-200 rounded-md text-sm py-1.5 px-2 font-mono" /></td>
                                        <td className="p-1"><input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} className="w-full border border-stone-200 rounded-md text-sm py-1.5 px-2" /></td>
                                        <td className="p-1"><input value={l.hsn_code} onChange={(e) => setLine(i, { hsn_code: e.target.value })} className="w-24 border border-stone-200 rounded-md text-sm py-1.5 px-2 font-mono" /></td>
                                        <td className="p-1"><input type="number" min="0" step="0.01" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} className="w-20 border border-stone-200 rounded-md text-sm py-1.5 px-2 text-right" /></td>
                                        <td className="p-1"><input type="number" min="0" step="0.01" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} className="w-24 border border-stone-200 rounded-md text-sm py-1.5 px-2 text-right" /></td>
                                        <td className="p-1"><input type="number" min="0" step="0.1" value={l.gst_rate} onChange={(e) => setLine(i, { gst_rate: e.target.value })} className="w-16 border border-stone-200 rounded-md text-sm py-1.5 px-2 text-right" /></td>
                                        <td className="p-1 text-right text-stone-700 font-medium">{inr((Number(l.qty) || 0) * (Number(l.rate) || 0))}</td>
                                        <td className="p-1"><button type="button" onClick={() => removeLine(i)} className="text-stone-400 hover:text-red-700 text-xs">×</button></td>
                                    </tr>
                                ))}
                                {lines.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-stone-500 text-sm">No lines. Add one to start.</td></tr>}
                            </tbody>
                        </table>
                        <datalist id="part-codes">{items.map(i => <option key={i.id} value={i.part_code}>{i.description}</option>)}</datalist>
                    </div>
                    <button type="button" onClick={addLine} className="text-sm text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"><Plus size={13} /> Add line</button>

                    <div className="flex justify-end text-sm gap-8">
                        <div className="text-stone-600">Subtotal: <span className="font-medium text-stone-900 ml-2">{inr(subtotal)}</span></div>
                        <div className="text-stone-600">GST: <span className="font-medium text-stone-900 ml-2">{inr(tax)}</span></div>
                        <div className="text-stone-600">Total: <span className="font-heading font-semibold text-amber-700 ml-2">{inr(subtotal + tax)}</span></div>
                    </div>
                        </>
                    )}

                    <textarea placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" rows={2} />

                    <div className="flex justify-end gap-3 border-t border-stone-200 pt-4">
                        <button type="button" onClick={onClose} className="text-sm text-stone-600 hover:text-stone-900 px-4 py-2 rounded-md">Cancel</button>
                        <button data-testid="save-quotation" type="submit" disabled={busy || !vendorId || (mode === "manual" ? lines.length === 0 : !uploadFile)} className="bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white rounded-md px-4 py-2 text-sm font-medium">
                            {busy ? "Saving…" : (mode === "upload" ? "Upload & submit" : "Submit quotation")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
